// Implements: F-USG-006, F-USG-010
/**
 * `GET /mockups/{id}/version-costs` (mockup v3 P7, contract C7.1): what each version cost.
 *
 * It lives in `shared/api` because three modules read it (the version chip and its timeline,
 * the drawer's "Detalles", the per-version breakdown of Mi consumo) and a module may not reach
 * into another; they share one query key (`versionCostsKey`), so the same versions are asked
 * for once. The server takes 1..50 ids a call: more are split into chunks of 50, one request
 * per chunk, never one per version. A read is a `GET`: the client's retry of a 5xx applies.
 *
 * Who sees it (P7-1, owner default): anyone who may edit the mockup. `useEditorVersionCosts`
 * asks only when the caller's `your_role` on the mockup is `owner` or `editor`; Mi consumo
 * shows the caller's own mockups and reads `useVersionCosts` directly.
 */

import { useQuery } from "@tanstack/react-query";

import type { MockupDetailView } from "../../types/api";
import type { FailedRun, VersionCostItem, VersionCostsView } from "../../types/costs";
import { ApiError, UNKNOWN_ERROR_CODE } from "./ApiError";
import { API_BASE } from "./apiBase";
import { httpRequest } from "./httpClient";
import { fetchMockupDetail } from "./mockupDetail";
import { mockupDetailKey, versionCostsKey } from "./queryKeys";

/** The most ids one call may carry (C7.1). */
export const VERSION_COSTS_CHUNK = 50;

const EDITOR_ROLES: ReadonlySet<string> = new Set(["owner", "editor"]);
const FORBIDDEN = 403;
const VERSION_COSTS_STALE_MS = 60_000;
const NOT_FOUND = 404;

function versionCostsPath(mockupId: string, versionIds: readonly string[]): string {
  const query = new URLSearchParams(versionIds.map((id) => ["version_id", id]));
  return `${API_BASE}/mockups/${encodeURIComponent(mockupId)}/version-costs?${query.toString()}`;
}

/** The costs of `versionIds` (each once, in the order given), in as few calls as the bound allows. */
export async function fetchVersionCosts(
  mockupId: string,
  versionIds: readonly string[],
  signal?: AbortSignal,
): Promise<VersionCostsView> {
  const ids = [...new Set(versionIds)];
  if (ids.length === 0) return { items: [], failed_runs: [] };
  const chunks: string[][] = [];
  for (let start = 0; start < ids.length; start += VERSION_COSTS_CHUNK) {
    chunks.push(ids.slice(start, start + VERSION_COSTS_CHUNK));
  }
  const views = await Promise.all(
    chunks.map(async (chunk) => {
      const view = await httpRequest<VersionCostsView>(versionCostsPath(mockupId, chunk), { signal });
      if (view === undefined) {
        throw new ApiError(200, UNKNOWN_ERROR_CODE, "GET /version-costs answered with no body", new Headers());
      }
      return view;
    }),
  );
  // The failed runs are the mockup's, the same in every chunk: they are kept once.
  return { items: views.flatMap((view) => view.items), failed_runs: views[0]?.failed_runs ?? [] };
}

export interface VersionCostsRead {
  readonly status: "pending" | "error" | "success";
  /** The cost of each asked version, by id; `undefined` until the answer for THESE ids arrives. */
  readonly costs: ReadonlyMap<string, VersionCostItem> | undefined;
  /** The mockup's newest failed or cancelled runs. */
  readonly failedRuns: readonly FailedRun[];
  /** Why the read failed, or `null`. */
  readonly error: ApiError | null;
  /** Reads the costs again (an error state's retry). */
  readonly refetch: () => void;
}

/**
 * The costs of some versions of one mockup. The key carries the mockup and the ids, and no
 * earlier answer is shown in the meantime: an answer for another mockup can never be read as
 * this one's (checklist 14).
 */
export function useVersionCosts(mockupId: string, versionIds: readonly string[], enabled = true): VersionCostsRead {
  const query = useQuery<VersionCostsView, ApiError>({
    queryKey: versionCostsKey(mockupId, versionIds),
    queryFn: ({ signal }) => fetchVersionCosts(mockupId, versionIds, signal),
    enabled: enabled && versionIds.length > 0,
    // A version's cost does not move once its run closed: a second view of the same versions (the
    // drawer after the chip) reads the cached answer instead of asking again.
    staleTime: VERSION_COSTS_STALE_MS,
  });
  const refetch = (): void => {
    void query.refetch();
  };
  if (query.data === undefined) {
    return { status: query.isError ? "error" : "pending", costs: undefined, failedRuns: [], error: query.error, refetch };
  }
  return {
    status: "success",
    costs: new Map(query.data.items.map((item) => [item.version_id, item])),
    failedRuns: query.data.failed_runs,
    error: null,
    refetch,
  };
}

/**
 * Whether the caller may see the mockup's costs (P7-1: whoever may edit it), from the shared
 * `GET /mockups/{id}` read: `pending` until it answers, `reader` for a viewer, `failed` when it
 * cannot be read.
 */
export type CostAccess = "pending" | "editor" | "reader" | "failed";

export function useCostAccess(mockupId: string): CostAccess {
  const mockup = useQuery<MockupDetailView, ApiError>({
    queryKey: mockupDetailKey(mockupId),
    queryFn: () => fetchMockupDetail(mockupId),
  });
  if (mockup.data !== undefined) return EDITOR_ROLES.has(mockup.data.your_role) ? "editor" : "reader";
  return mockup.isError ? "failed" : "pending";
}

/** `useVersionCosts` for whoever may edit the mockup (P7-1); nothing is asked for anybody else. */
export function useEditorVersionCosts(mockupId: string, versionIds: readonly string[]): VersionCostsRead {
  return useVersionCosts(mockupId, versionIds, useCostAccess(mockupId) === "editor");
}

/** A refusal that means "these costs are not yours to see" (a role lost since the page read it). */
export function isCostRefusal(error: ApiError | null): boolean {
  return error !== null && (error.status === FORBIDDEN || error.status === NOT_FOUND);
}
